import { MonitorIcon, MoonIcon, SunIcon } from 'lucide-react';
import { makeAutoObservable } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { ThemeType } from '@type/themeType';
import firebaseStore from '@client/store/firebaseStore';
import modalStore from '@client/store/modalStore';
import webViewStore from '@client/store/webViewStore';
import Box from '@client/uikit/Box';
import Frame from '@client/uikit/Frame';
import { SettingButton, SettingLink } from '@client/uikit/SettingButton';
import { apiClient } from '@app/apiClient';
import { useMobxInstance } from '@client/clientUtils/mobxUtil';
import BuildVersion from '@client/component/BuildVersion';
import UserInfoSetupModal from '../my/UserInfoSetupModal';
import UserProfile from './UserProfile';
import themeStore from '@client/store/themeStore';

class ViewStore {
    constructor() {
        makeAutoObservable(this);
    }

    async fetch() {}
}

const MorePage = observer(() => {
    const viewStore = useMobxInstance(ViewStore);

    useEffect(() => {
        viewStore.fetch();
    }, [viewStore]);

    return (
        <Frame>
            <Frame.Title title="더보기" />
            <Box>
                <UserProfile />
            </Box>
            {/* 설정 메뉴 */}
            <Box>
                <Box.Title title="설정" />
                <SettingButton
                    title="테마 설정"
                    getCurrentValue={async () => {
                        if (!themeStore.savedTheme) {
                            return '시스템 설정';
                        } else if (themeStore.savedTheme === 'light') {
                            return '라이트 모드';
                        } else if (themeStore.savedTheme === 'dark') {
                            return '다크 모드';
                        } else {
                            return null;
                        }
                    }}
                    onClick={async (updateCurrentValue) => {
                        const savedTheme = themeStore.savedTheme;

                        const { returnValue: selectedTheme } = await modalStore.select<ThemeType>({
                            title: '테마 설정',
                            selectedValue: savedTheme || 'system',
                            showRadio: true,
                            closeOnSelect: false,
                            confirmText: '적용하기',
                            items: [
                                {
                                    name: '시스템 설정 따르기',
                                    value: 'system',
                                    icon: <MonitorIcon className="size-5" />,
                                },
                                {
                                    name: '라이트 모드',
                                    value: 'light',
                                    icon: <SunIcon className="size-5" />,
                                },
                                {
                                    name: '다크 모드',
                                    value: 'dark',
                                    icon: <MoonIcon className="size-5" />,
                                },
                            ],
                        });

                        if (selectedTheme && selectedTheme !== savedTheme) {
                            themeStore.setTheme(selectedTheme, true);
                            updateCurrentValue();
                        }
                    }}
                />
            </Box>

            {/* <Box>
                <Box.Title title="스토리" />
                <SettingLink title="해와오 개발일지" linkTo="/channel/devlog" />
            </Box> */}

            {import.meta.env.DEV && (
                <Box>
                    <Box.Title title="개발 전용" />
                    <SettingLink title="브리지 테스트" linkTo="/bridge-test" />
                    <SettingLink title="딥링크 테스트" linkTo="/deeplink-test" />
                    <SettingLink title="가독성 테스트" linkTo="/readability-test" />
                    <SettingButton
                        title="가입 테스트"
                        onClick={async () => {
                            const result = await modalStore.custom({
                                renderComponent: ({ modalId }) => (
                                    <UserInfoSetupModal modalId={modalId} />
                                ),
                            });

                            alert(JSON.stringify(result));
                        }}
                    />
                    <SettingButton
                        title="앱 재시작 테스트"
                        onClick={async () => {
                            webViewStore.sendMessageToApp('reloadApp');
                        }}
                    />
                </Box>
            )}

            <Box>
                <Box.Title title="서비스 정보" />
                <SettingLink title="이용약관" linkTo="/terms" />
                <SettingLink title="개인정보처리방침" linkTo="/privacy" />
                <SettingLink title="오픈소스 라이선스" linkTo="/more/license" />
                <SettingButton
                    title="사업자정보"
                    onClick={async () => {
                        modalStore.custom({
                            title: '사업자정보',
                            renderComponent: ({ modalId }) => (
                                <div className="text-base-content/80 text-sm leading-relaxed">
                                    상호: 주식회사 해와오
                                    <br />
                                    대표: 안오균
                                    <br />
                                    사업자등록번호: 520-87-03439
                                    <br />
                                    주소: 경기도 용인시 수지구 수지로 342번길 34, 4,5층 552호
                                    <br />
                                    문의: haewao@haewao.com
                                </div>
                            ),
                        });
                    }}
                />
            </Box>
            {/* 로그아웃 버튼 */}
            {firebaseStore.me && (
                <div className="ml-5 flex flex-col items-start gap-2">
                    <button
                        className="link link-hover text-base-content/70 self-start text-sm"
                        onClick={async (e) => {
                            e.preventDefault();

                            const { returnValue } = await modalStore.confirm({
                                title: '로그아웃',
                                message: '로그아웃하시겠습니까?',
                                preventClose: false,
                            });

                            if (returnValue) {
                                firebaseStore.logout();
                            }
                        }}
                    >
                        로그아웃
                    </button>
                    <button
                        className="link link-hover text-base-content/70 self-start text-sm"
                        onClick={async (e) => {
                            e.preventDefault();

                            const { returnValue } = await modalStore.confirm({
                                title: '정말 탈퇴할까요?',
                                message: '탈퇴한 계정은 되돌릴 수 없습니다.',
                                confirmText: '탈퇴하기',
                                confirmStyle: 'danger',
                                cancelText: '취소',
                                preventClose: false,
                                checkbox: {
                                    text: '모든 데이터를 삭제하는 것에 동의합니다',
                                    required: true,
                                },
                            });

                            if (returnValue) {
                                try {
                                    await apiClient.firebase.member.deleteAccount.mutate();

                                    // 탈퇴 성공 후 로그아웃 처리 (로컬 상태 정리)
                                    await firebaseStore.logout();
                                } catch (error) {
                                    console.error('탈퇴 처리 중 오류:', error);
                                    await modalStore.alert({
                                        title: '오류',
                                        message: '탈퇴 처리 중 오류가 발생했습니다.',
                                    });
                                }
                            }
                        }}
                    >
                        탈퇴하기
                    </button>
                </div>
            )}
            <Frame.Spacer />
            <Box noBorder={true} className="flex flex-col items-start gap-3 bg-transparent">
                <BuildVersion />
            </Box>
            <Frame.BottomSpacer />
        </Frame>
    );
});

export default MorePage;
